import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";
import { encrypted } from "../Common/encrypted";
import {
  removeItem,
  removeItemWithExpiry,
  storeItem,
} from "../../lib/utility";
import {
  extractPermissionsFromResponse,
  matchAccessMemberId,
  resolveAbacAccessId,
  resolveAbacAccessName,
  resolveAbacListUserId,
} from "../User/Abac/abacApi";
import {
  deepClone,
  filterAbacTreeForParentRole,
} from "../User/Abac/abacPermissionUtils";

/** Drop role-hidden / orphan leaves so stale API `true` values cannot keep menus open. */
function sanitizeSessionAbacPermissions(permissions, user) {
  if (!permissions || typeof permissions !== "object") return {};
  const next = deepClone(permissions);
  const role =
    user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "") || "";
  filterAbacTreeForParentRole(next, role);
  return next;
}

export const AUTH_USER_STORAGE_KEY = "authUser";

const initialState = {
  user: {},
  accessDevice: false,
  otpScreen: false,
  confirmOtp: false,
  accountExpired: false,
  isFetching: false,
  isLoginSuccess: false,
  isOTPSuccess: false,
  isAccessDeviceSuccess: false,
  isError: false,
  errorMessage: "",
  /** Captured from /login/jwt only; response filled on final login success */
  jwtLoginMeta: null,

  /** Live ABAC permissions for the logged-in multi-access user (null = fail-open). */
  abacPermissions: null,
  abacAccessKey: null,
  isFetchingAbacSession: false,
  /** True after any completed attempt (success or failure). */
  abacSessionLoaded: false,
  /** True only when the last permissions GET succeeded. */
  abacSessionOk: false,
  /** Date.now() when the last attempt finished. */
  abacSessionFetchedAt: 0,
  /** Last applied websocket push version per accessId (dedupe = accessId + version). */
  abacPushVersions: {},

  navOptions: {},
  isFetchingNavOptions: false,
  isnavOptionsFetchSuccess: false,
  isnavOptionsFetchError: false,
  navOptionsFetchErrorMessage: "",

  isFetchingLandingInfo: false,

  isFetchingLandingInfoCreate: false,
  isSuccessCreateLandingInfo: false,

  isFetchingLoginPanelSave: false,
  isErrorUpdateLandingInfo: false,
  updateLandingInfoErrorMsg: "",
  isFetchingLandingInfoAll: false,
};

function clearStaleAuthSession() {
  removeItemWithExpiry("profileData");
  removeItem("userId");
  sessionStorage.removeItem("forgotPasswordUserName");
  globalThis.profileData = null;
  globalThis.isAuthenticated = false;
  globalThis.isFirstTime = true;
}

function resetAbacSession(state) {
  state.abacPermissions = null;
  state.abacAccessKey = null;
  state.isFetchingAbacSession = false;
  state.abacSessionLoaded = false;
  state.abacSessionOk = false;
  state.abacSessionFetchedAt = 0;
  state.abacPushVersions = {};
}

function applyLoginSuccess(state, data, extras = {}) {
  clearStaleAuthSession();
  state.otpScreen = false;
  state.accessDevice = false;
  state.accountExpired = false;
  state.confirmOtp = false;

  // Only the access-device form marks a member. Live JWTs often include
  // accessName for parents too — using that here gated BroadAdmin after deploy.
  const accessName = String(extras.accessName ?? "").trim();
  state.user = accessName
    ? {
        ...data,
        accessName,
        isMultiAccessMember: true,
        abacMemberLogin: true,
      }
    : { ...data, isMultiAccessMember: false, abacMemberLogin: false };
  state.isLoginSuccess = true;
  resetAbacSession(state);
  globalThis.isAuthenticated = true;
  if (data?.id != null) {
    storeItem("userId", data.id);
  }
  // Keep /login/jwt requestUrl; only set response fields from final success payload
  state.jwtLoginMeta = {
    requestUrl: state.jwtLoginMeta?.requestUrl ?? "",
    response: {
      accessName: data?.accessName ?? "",
      isMultiSessionAllowed: data?.isMultiSessionAllowed,
      accessToken: data?.accessToken ?? "",
    },
    capturedAt: state.jwtLoginMeta?.capturedAt ?? Date.now(),
  };
}

const getIPAddress = async () => {
  try {
    let { data } = await axios.get("https://api.ipify.org/");
    if (data) return data;
  } catch (err) {
    showToast(
      "Error Occured while getting your IP. Please Refresh page.",
      "error"
    );
  }
};
export const loginUser = createAsyncThunk(
  "auth/login",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      const encryptedData = encrypted({ ...udata, ipAddress });
      const response = await axios.post(
        "/login/jwt",
        {},
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (response.status === 200) {
        let requestUrl = "";
        try {
          requestUrl =
            (typeof axios.getUri === "function" && response.config
              ? axios.getUri(response.config)
              : "") ||
            response.request?.responseURL ||
            "";
        } catch {
          requestUrl = response.request?.responseURL || "";
        }
        return {
          data,
          status: headers.get("X-Custom-Info") ?? status,
          requestUrl,
        };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
/**
 * Initial ABAC permissions for a multi-access member session (after login / reload).
 * Websocket pushes only arrive when an admin changes permissions, so the current
 * saved permissions must be fetched once per session.
 */
export const loadAbacSessionPermissions = createAsyncThunk(
  "auth/abac-session-permissions",
  async (_, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const user = auth?.user;
    const accessToken = user.accessToken;
    const startedAt = Date.now();
    const headers = { Authorization: `Bearer ${accessToken}` };
    try {
      let accessId = resolveAbacAccessId(user);
      if (accessId == null) {
        const listUserId = resolveAbacListUserId(user);
        if (listUserId != null) {
          const accessName = resolveAbacAccessName(user);
          const { data: listData } = await axios.get("/multiUser/list/access", {
            params: {
              userId: listUserId,
              ...(accessName ? { search: accessName } : {}),
              pageNumber: 0,
              pageSize: 50,
            },
            headers,
          });
          accessId = matchAccessMemberId(listData, accessName);
        }
      }
      if (accessId == null) {
        console.log("[ABAC] session permissions skipped — access id not found", user);
        return thunkAPI.rejectWithValue(null);
      }

      const { data, status } = await axios.get(
        `/multiUser/permissions/${accessId}`,
        { headers }
      );
      if (!status.toString().startsWith("2")) {
        return thunkAPI.rejectWithValue(data);
      }
      const permissions = sanitizeSessionAbacPermissions(
        extractPermissionsFromResponse(data, accessId),
        user
      );
      console.log("[ABAC] session permissions loaded", { accessId, permissions });
      return { accessId, accessToken, permissions, startedAt };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data ?? null);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { auth } = getState();
      if (!auth?.user?.accessToken) return false;
      if (auth.isFetchingAbacSession) return false;
      if (arg?.force) return true;
      return !auth.abacSessionLoaded;
    },
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let accessToken = auth.user.accessToken;
      const response = await axios.post(
        "/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      let { data, status, headers } = response;

      if (response.status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      let encryptedData = encrypted({ ...udata, ipAddress });

      const response = await axios.post(
        "/login/verifyOtp",
        {},
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const accessDeviceThunk = createAsyncThunk(
  "auth/authenticateMultiUser",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      let encryptedData = encrypted({ ...udata, ipAddress });
      const response = await axios.post(
        "/login/authenticateMultiUser",
        encryptedData,
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const accessDeviceSkip = createAsyncThunk(
  "auth/accessDeviceSkip",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      let encryptedData = encrypted({ ...udata, ipAddress });

      const response = await axios.post(
        "/login/authenticateSkip",
        encryptedData,
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);


export const getNavOptionsData = createAsyncThunk(
  "login/web-access",
  async (_, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let { data, status } = await axios.get(`/login/web-access`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getLandingInfo = createAsyncThunk(
  "login/user/data",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let { data, status } = await axios.get(`users/panel/public/` + udata, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (status.toString().startsWith("2")) {
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const updateLandingInfo = createAsyncThunk(
  "update/landingInfo",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let systemId = auth.user.username;

      const { content, logo, icons, refId } = udata;

      const formData = new FormData();
      formData.append("logo", logo);
      Array.from(icons).forEach((icon, index) => {
        formData.append("icons", icon);
      });
      formData.append("content", content);

      const { data, status } = await axios.put(
        `/users/panel/${refId}?systemId=` + systemId,
        formData,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const createLandingInfo = createAsyncThunk(
  "create/landingInfo",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let systemId = auth.user.username;

      const { content, logo, icons, refId } = udata;

      const formData = new FormData();
      formData.append("logo", logo);
      Array.from(icons).forEach((icon, index) => {
        formData.append("icons", icon);
      });
      formData.append("content", content);

      let { data, status } = await axios.post(
        `/users/panel?systemId=` + systemId,
        formData,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearStateAuth: (state, { payload }) => {
      state.isFetching = false;
      state.accessDevice = false;
      state.otpScreen = false;
      state.confirmOtp = false;
      state.accountExpired = false;
      state.isError = false;
      state.errorMessage = "";
      state.isLoginSuccess = false;
    },
    logoutAuth: () => {
      clearStaleAuthSession();

      localStorage.removeItem("savedSearch");
      localStorage.removeItem("dateRange");
      localStorage.removeItem("pinnedGraph");
      // Fresh login → sidebar sections / nested menus start closed again
      localStorage.removeItem("sidebarSectionsOpen");
      localStorage.removeItem("sideActive");

      if (globalThis.stallTime) {
        clearInterval(globalThis.stallTime);
        globalThis.stallTime = null;
        globalThis.notificationTime = null;
      }

      return { ...initialState };
    },
    clearPanelUpdate: (state) => {
      state.isFetchingLoginPanelSave = false;
      state.isErrorUpdateLandingInfo = false;
      state.updateLandingInfoErrorMsg = "";
    },
    /**
     * Websocket ABAC_PERMISSIONS_UPDATED events replace the whole tree each time.
     */
    applyAbacPermissionsPush: (state, { payload }) => {
      const { accessId, accessName, version, permissions } = payload || {};
      state.abacPermissions = permissions ?? {};
      if (accessId != null) state.abacAccessKey = accessId;
      state.abacSessionLoaded = true;
      state.abacSessionOk = true;
      state.abacSessionFetchedAt = Date.now();
      if (accessId != null && version != null) {
        state.abacPushVersions = {
          ...(state.abacPushVersions || {}),
          [String(accessId)]: version,
        };
      }
      if (
        accessId != null &&
        state.user?.abacMemberLogin === true &&
        typeof state.user === "object"
      ) {
        state.user = {
          ...state.user,
          accessId,
          ...(accessName ? { accessName } : {}),
        };
        if (state.user.accessToken) {
          storeItem(AUTH_USER_STORAGE_KEY, state.user);
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadAbacSessionPermissions.pending, (state) => {
        state.isFetchingAbacSession = true;
      })
      .addCase(loadAbacSessionPermissions.fulfilled, (state, { payload }) => {
        state.isFetchingAbacSession = false;
        if (state.user?.accessToken !== payload.accessToken) return;
        // A websocket push that landed while this GET was in flight is newer.
        if (
          state.abacSessionLoaded &&
          state.abacSessionFetchedAt > (payload.startedAt ?? 0)
        ) {
          return;
        }
        state.abacPermissions = payload.permissions ?? {};
        state.abacAccessKey = payload.accessId;
        state.abacSessionLoaded = true;
        state.abacSessionOk = true;
        state.abacSessionFetchedAt = Date.now();
        state.user = { ...state.user, accessId: payload.accessId };
        if (state.user.accessToken) {
          storeItem(AUTH_USER_STORAGE_KEY, state.user);
        }
      })
      .addCase(loadAbacSessionPermissions.rejected, (state, action) => {
        if (action.meta?.condition) return;
        state.isFetchingAbacSession = false;
        if (!state.user?.accessToken) return;
        state.abacSessionFetchedAt = Date.now();
        if (state.abacSessionLoaded) return;
        state.abacSessionLoaded = true;
        state.abacSessionOk = false;
      })
      .addCase(accessDeviceSkip.fulfilled, (state, action) => {
        let { data, status } = action.payload;

        state.accessDevice = false;
        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data);
        }
        state.isFetching = false;
        state.isAccessDeviceSuccess = true;
      })
      .addCase(accessDeviceSkip.pending, (state) => {
        state.isFetching = true;
        state.isAccessDeviceSuccess = false;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(accessDeviceSkip.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(accessDeviceThunk.fulfilled, (state, action) => {
        let { data, status } = action.payload;

        state.accessDevice = false;
        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data, {
            accessName: action.meta?.arg?.accessName,
          });
        }
        state.isFetching = false;
        state.isAccessDeviceSuccess = true;
      })
      .addCase(accessDeviceThunk.pending, (state) => {
        state.isFetching = true;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(accessDeviceThunk.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload
          ? "Device Access does not matched"
          : "Internal error";
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        let { data, status } = action.payload;

        state.otpScreen = false;
        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data, {
            accessName: action.meta?.arg?.accessName,
          });
        }
        state.isFetching = false;
        state.isOTPSuccess = true;
      })
      .addCase(verifyOTP.pending, (state) => {
        state.isFetching = true;
        state.isOTPSuccess = false;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        let { data, status, requestUrl } = action.payload;

        // Stash jwt requestUrl immediately (including 001 / 002 / 003 paths)
        state.jwtLoginMeta = {
          requestUrl: requestUrl || state.jwtLoginMeta?.requestUrl || "",
          response: state.jwtLoginMeta?.response ?? null,
          capturedAt: Date.now(),
        };

        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data);
        }

        state.isFetching = false;
      })
      .addCase(loginUser.pending, (state) => {
        clearStaleAuthSession();
        state.user = {};
        state.jwtLoginMeta = null;
        state.isFetching = true;
        state.isLoginSuccess = false;
        state.isOTPSuccess = false;
        state.isAccessDeviceSuccess = false;
        state.errorMessage = "";
        state.isError = false;
        state.accessDevice = false;
        state.otpScreen = false;
        state.confirmOtp = false;
        state.accountExpired = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload
          ? action.payload.message
          : "Internal error";
      })

      .addCase(getNavOptionsData.fulfilled, (state, action) => {
        state.navOptions = action.payload;
        state.isFetchingNavOptions = false;
        state.isnavOptionsFetchSuccess = true;
      })
      .addCase(getNavOptionsData.pending, (state) => {
        state.isFetchingNavOptions = true;
        state.isnavOptionsFetchSuccess = false;
        state.isnavOptionsFetchError = false;
        state.navOptionsFetchErrorMessage = "";
      })
      .addCase(getNavOptionsData.rejected, (state, action) => {
        state.navOptions = {};
        state.isFetchingNavOptions = false;
        state.isnavOptionsFetchError = true;
        state.navOptionsFetchErrorMessage = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(getLandingInfo.pending, (state, action) => {
        state.isFetchingLandingInfo = true;
      })
      .addCase(getLandingInfo.fulfilled, (state, action) => {
        state.isFetchingLandingInfo = false;
      })
      .addCase(getLandingInfo.rejected, (state, action) => {
        state.isFetchingLandingInfo = false;
      })
      .addCase(updateLandingInfo.pending, (state, action) => {
        state.isFetchingLoginPanelSave = true;
      })
      .addCase(updateLandingInfo.fulfilled, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isErrorUpdateLandingInfo = false;
        state.updateLandingInfoErrorMsg = "";
      })
      .addCase(updateLandingInfo.rejected, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isErrorUpdateLandingInfo = true;
        state.updateLandingInfoErrorMsg = action.payload
          ? action.payload.message
          : "Error while updating panel";
      })
      .addCase(createLandingInfo.pending, (state, action) => {
        state.isFetchingLoginPanelSave = true;
      })
      .addCase(createLandingInfo.fulfilled, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isSuccessCreateLandingInfo = true;
      })
      .addCase(createLandingInfo.rejected, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isSuccessCreateLandingInfo = false;
      });
  },
});


export const {
  clearStateAuth,
  logoutAuth,
  clearPanelUpdate,
  applyAbacPermissionsPush,
} = authSlice.actions;

export const ABAC_PERMISSIONS_PUSH_EVENT_TYPE = "ABAC_PERMISSIONS_UPDATED";

function isNewerAbacPushVersion(next, last) {
  if (last == null) return true;
  const nextNum = Number(next);
  const lastNum = Number(last);
  if (Number.isFinite(nextNum) && Number.isFinite(lastNum)) {
    return nextNum > lastNum;
  }
  return String(next) !== String(last);
}

/**
 * Handle /user/queue/abac-permissions websocket events.
 * Payload: { type, accessId, parentUserId, accessName, updatedBy, updatedAt, version, permissions }.
 * Dedupe on accessId + version; ignore events for a different access than this session.
 */
export function handleAbacPermissionsPushEvent(rawEvent) {
  return (dispatch, getState) => {
    if (!rawEvent || typeof rawEvent !== "object") return;
    const envelope = rawEvent.payload ?? rawEvent.data;
    const event =
      rawEvent.accessId == null && envelope && typeof envelope === "object"
        ? { type: rawEvent.type ?? rawEvent.eventType, ...envelope }
        : rawEvent;
    const type = String(event.type || event.eventType || "").toUpperCase();
    if (type && type !== ABAC_PERMISSIONS_PUSH_EVENT_TYPE) {
      console.log("[ABAC WS] skipped — unexpected event type:", type);
      return;
    }

    const { auth } = getState();
    if (!auth?.user?.accessToken) {
      console.log("[ABAC WS] skipped — not logged in");
      return;
    }
    if (auth.user?.abacMemberLogin !== true) {
      console.log("[ABAC WS] skipped — parent session");
      return;
    }

    const accessId = event.accessId ?? null;
    const sessionAccessId = auth.abacAccessKey ?? null;
    if (
      accessId != null &&
      sessionAccessId != null &&
      String(accessId) !== String(sessionAccessId)
    ) {
      console.log("[ABAC WS] skipped — accessId mismatch", {
        eventAccessId: accessId,
        sessionAccessId,
      });
      return;
    }

    const version = event.version ?? event.updatedAt ?? null;
    const dedupeKey = accessId ?? sessionAccessId;
    if (dedupeKey != null && version != null) {
      const last = auth.abacPushVersions?.[String(dedupeKey)];
      if (!isNewerAbacPushVersion(version, last)) {
        console.log("[ABAC WS] skipped — duplicate/old version", {
          accessId: dedupeKey,
          version,
          lastApplied: last,
        });
        return;
      }
    }

    let permissions = null;
    if (event.permissions && typeof event.permissions === "object") {
      permissions = extractPermissionsFromResponse(event.permissions, dedupeKey);
      if (!Object.keys(permissions).length) {
        permissions = extractPermissionsFromResponse(
          { permissions: event.permissions },
          dedupeKey
        );
      }
      // All-off save may flatten to {} — still apply so member gets No Access.
      if (!permissions || typeof permissions !== "object") {
        permissions = {};
      }
      permissions = sanitizeSessionAbacPermissions(permissions, auth.user);
    } else {
      console.log("[ABAC WS] skipped — no permissions field in event", event);
      return;
    }

    console.log("[ABAC WS] applied permissions update", {
      accessId: dedupeKey,
      version,
      accessName: event.accessName,
      updatedBy: event.updatedBy,
      updatedAt: event.updatedAt,
      permissions,
    });
    dispatch(
      applyAbacPermissionsPush({
        accessId: dedupeKey,
        accessName: event.accessName,
        version,
        permissions,
      })
    );
  };
}

export function clearState() {
  return async (dispatch) => {
    dispatch(clearStateAuth());
  };
}

export function clearLogoutAuthState() {
  return async (dispatch) => {
    dispatch(logoutAuth());
  };
}


export const authSelector = (state) => state.auth;

export default authSlice.reducer;

